import { useCallback, useState, useMemo, useContext } from "react"

import { ORDERBOOK_DEPTH } from "../../../api/constants";
import { Side } from "../../../api/enum";
import { ClientQuoteResponse } from "../../../api/response";
import { U2OptionInfos } from "../../../functions/OptionInfo";
import { LanguageMap } from "../../../functions/Language";
import { APIContext } from "../..";

import { Button, Card, Form, InputGroup, Table, Modal } from "react-bootstrap";

export interface ControlPanelProps {
    quotes: {[key:string]: ClientQuoteResponse|undefined}
    setSelectedUnderlying: React.Dispatch<React.SetStateAction<number|null>>
    setSelectedSide: React.Dispatch<React.SetStateAction<Side|null>>
}

const DEFAULT_MULTIPLIER : number = 1
const DEFAULT_LEVELS     : number = 3

function LoadUList(){
    const localConfig = localStorage.getItem("orderbook_screener_ulist")
    if (localConfig)
        return JSON.parse(localConfig) as number[]
    return Array.from(U2OptionInfos.keys()).map(x=>x).sort((a,b)=>a-b).filter(x=>x<10000)
}

function ControlPanel({ quotes, setSelectedUnderlying, setSelectedSide }: ControlPanelProps) {
    const { language } = useContext(APIContext)
    const textMap = useMemo(()=>LanguageMap.get(language)!.page.orderbook_screener.control_panel, [language])

    // ui
    const [uiSelectedSide, setUISelectedSide] = useState<Side|null>(Side.kBid)
    const [uiSupportMode, setUISupportMode] = useState<boolean>(false)

    // config
    const ulist = useMemo(()=>Array.from(U2OptionInfos.keys()).filter((v)=>v<10000).sort((a,b)=>a-b), [])
    const [selectedUList, setSelectedUList] = useState<number[]>(LoadUList())
    const [showResetModal, setShowResetModal] = useState<boolean>(false)
    const resetUList = useCallback(()=>{
        localStorage.setItem("orderbook_screener_ulist", JSON.stringify(ulist))
        setSelectedUList(ulist)
        setShowResetModal(false)
    }, [ulist])
    const modifyUlist = useCallback((u: number, enable: boolean)=>{
        setSelectedUList((list)=>{
            if (!enable) {
                const newlist = list.filter((x)=>u!==x)
                localStorage.setItem("orderbook_screener_ulist", JSON.stringify(newlist))
                return newlist
            }

            if (list.indexOf(u) === -1) {
                const newlist = [...list, u]
                localStorage.setItem("orderbook_screener_ulist", JSON.stringify(newlist))
                return newlist
            }

            return list
        })
    }, [])

    // value
    const [filter, setFilter] = useState<boolean>(false)
    const [side, setSide] = useState<Side|null>(null)
    const [multiplier, setMultiplier] = useState<number>(DEFAULT_MULTIPLIER)
    const [levels, setLevels] = useState<number>(DEFAULT_LEVELS)
    const [supportMode, setSupportMode] = useState<boolean>(false)
    const [onlyBest, setOnlyBest] = useState<boolean>(false)
    
    const results = useMemo(()=>{
        const filteredU : {multiplier?: number, quote: ClientQuoteResponse}[] = Object.entries(quotes).map((v) => v[1]!).filter((v)=>U2OptionInfos.has(v.symbol_)).map((v)=>{
            // hide which is not on list
            if (selectedUList.indexOf(v.symbol_) === -1) return { quote: v }
            // if (v.symbol_ === 1347)console.log(v.symbol_)

            // filter not on
            if (!filter) return { multiplier: 0, quote: v }

            if (supportMode) {
                if (side === Side.kBid) {
                    if (U2OptionInfos.get(v.symbol_)!.put.length === 0) return { quote: v }

                    let next_n_level_sum = 0
                    for (let j = 0; j < levels ; ++j)
                        next_n_level_sum += v.bid_size_[j]

                    if (v.ask_size_[0] > next_n_level_sum * multiplier) return { multiplier: v.ask_size_[0] / next_n_level_sum, quote: v }
                }
                else if (side === Side.kAsk) {
                    if (U2OptionInfos.get(v.symbol_)!.call.length === 0) return { quote: v }

                    let next_n_level_sum = 0
                    for (let j = 0; j < levels ; ++j)
                        next_n_level_sum += v.ask_size_[j]
                    
                    if (v.bid_size_[0] > next_n_level_sum * multiplier) return { multiplier: v.bid_size_[0] / next_n_level_sum, quote: v }
                }
            }
            else {
                if (side === Side.kBid) {
                    if (U2OptionInfos.get(v.symbol_)!.put.length === 0) return { quote: v }

                    for (let i = 0; i < (!onlyBest?(ORDERBOOK_DEPTH - levels):1); i++){
                        let next_n_level_sum = 0
                        for (let j = i + 1; j < i + levels + 1; j++)
                            next_n_level_sum += v.bid_size_[j]
                        if (v.bid_size_[i] > next_n_level_sum * multiplier) return { multiplier: v.bid_size_[i] / next_n_level_sum, quote: v }
                    }
                }
                else if (side === Side.kAsk) {
                    if (U2OptionInfos.get(v.symbol_)!.call.length === 0) return { quote: v }
                    
                    for (let i = 0; i < (!onlyBest?(ORDERBOOK_DEPTH - levels):1); i++){
                        let next_n_level_sum = 0
                        for (let j = i + 1; j < i + levels + 1; j++)
                            next_n_level_sum += v.ask_size_[j]
                        if (v.ask_size_[i] > next_n_level_sum * multiplier) return { multiplier: v.ask_size_[i] / next_n_level_sum, quote: v }
                    }
                }
            }

            return { quote: v }
        }).filter((v)=>typeof(v.multiplier) !== "undefined").sort((a,b)=>b.multiplier!-a.multiplier!)

        return filteredU
    }, [quotes, selectedUList, filter, side, multiplier, levels, supportMode, onlyBest])

    const onsubmit = useCallback<React.FormEventHandler<HTMLFormElement>>((event)=>{
        event.preventDefault()
        event.stopPropagation()

        const formData = new FormData(event.currentTarget)
        const multiplier = parseFloat(formData.get("multiplier")!.toString())
        const levels = parseInt(formData.get("levels")!.toString())
        const onlyBest = (formData.get("onlyBest")!)?true:false

        setFilter(true)
        setMultiplier(multiplier)
        setLevels(levels)
        setSide(uiSelectedSide)
        setSupportMode(uiSupportMode)
        setOnlyBest(onlyBest)

        setSelectedSide(uiSelectedSide)
    }, [uiSelectedSide, uiSupportMode, setSelectedSide])

    return useMemo(()=>{
        return (
            <Card style={{flex: 3, minWidth: 300, flexShrink: 0}}>
                <Card.Header>{textMap.name}</Card.Header>
                <Card.Body style={{display: "flex", flexDirection: "row", gap: 16, padding: "0.75rem", maxHeight: "28.75rem"}}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column", gap: "0.25rem"}}>
                        <Form onSubmit={onsubmit} style={{flex: 0, display: "flex", flexDirection: "column", gap: "0.25rem"}}>
                            <span>{textMap.orderbook_volume_search}</span>
                            <InputGroup>
                                <Button style={{flex: 1}} className={uiSelectedSide===Side.kBid?"btn-success":"btn-secondary"} onClick={()=>setUISelectedSide(Side.kBid)}>
                                    {textMap.bid}
                                </Button>
                                <Button style={{flex: 1}} className={uiSelectedSide===Side.kAsk?"btn-danger":"btn-secondary"} onClick={()=>setUISelectedSide(Side.kAsk)}>
                                    {textMap.ask}
                                </Button>
                            </InputGroup>
                            <InputGroup>
                                <InputGroup.Text>{textMap.multiplier}</InputGroup.Text>
                                <Form.Control type="number" name="multiplier" min={0.25} step={0.25} required defaultValue={DEFAULT_MULTIPLIER}/>
                            </InputGroup>
                            <InputGroup>
                                <InputGroup.Text>{textMap.levels}</InputGroup.Text>
                                <Form.Control type="number" name="levels" min={1} max={9} step={1} required defaultValue={DEFAULT_LEVELS}/>
                            </InputGroup>
                            <Form.Check type="switch" label={textMap.supportMode} checked={uiSupportMode} name="supportMode" style={{margin: 0}} onChange={(e)=>setUISupportMode(e.currentTarget.checked)}/>
                            <Form.Check type="switch" label={textMap.bestLevelOnly} defaultChecked={false} name="onlyBest" style={{margin: 0}} disabled={uiSupportMode}/>
                            <Button type="submit">{textMap.search}</Button>
                        </Form>
                        <div style={{flex: 1, overflowY: "auto"}}>
                            <Modal show={showResetModal}>
                                <Modal.Header>
                                    <Modal.Title>{textMap.confirmReset}</Modal.Title>
                                </Modal.Header>
                                <Modal.Footer>
                                    <Button onClick={()=>resetUList()}>{textMap.yes}</Button>
                                    <Button onClick={()=>setShowResetModal(false)}>{textMap.no}</Button>
                                </Modal.Footer>
                            </Modal>
                            <Table border={1} striped size="sm" style={{marginBottom: 0}}>
                                <thead className="bg-light sticky-top top-0" style={{boxShadow: "0 -1px 0 #dee2e6, inset 0 -1px 5px #dee2e6"}}>
                                    <tr>
                                        <th>{textMap.screeningUCode}</th>
                                        <th style={{textAlign: "right"}}>
                                            <Button size="sm" style={{padding: "0px 8px", fontSize: 14, width: 80}} onClick={()=>setShowResetModal(true)}>{textMap.reset}</Button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        ulist.map((x)=>{
                                            return (
                                                <tr key={x}>
                                                    <td>{x}</td>
                                                    <td style={{textAlign: "right"}}>
                                                        <Form.Check type="switch" checked={selectedUList.indexOf(x) !== -1} onChange={(e)=>modifyUlist(x, e.currentTarget.checked)}/>
                                                    </td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </Table>
                        </div>
                    </div>
                    <div style={{flex: 1, overflowY: "auto"}}>
                        <Table border={1} striped size="sm" style={{marginBottom: 0}}>
                            <thead className="bg-light sticky-top top-0" style={{boxShadow: "0 -1px 0 #dee2e6, inset 0 -1px 5px #dee2e6"}}>
                                <tr>
                                    <th>{textMap.ucode}</th>
                                    <th style={{textAlign: "right"}}>{textMap.multix}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    results.map((v)=>{
                                        return (
                                            <tr key={v.quote.symbol_}>
                                                <td>{v.quote.symbol_}</td>
                                                <td style={{textAlign: "right"}}>{(Math.round(v.multiplier! * 100) / 100).toFixed(2)}</td>
                                                <td style={{textAlign: "right"}}>
                                                    <Button style={{padding: "0px 8px", fontSize: 14}} onClick={()=>setSelectedUnderlying(v.quote.symbol_)}>{textMap.select}</Button>
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        )
    }, [textMap, onsubmit, ulist, resetUList, modifyUlist, selectedUList, showResetModal, uiSelectedSide, uiSupportMode, results, setSelectedUnderlying])
}

export default ControlPanel